import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import {
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  Toast,
  Toaster,
  ToastTitle,
  tokens,
  useToastController,
} from "@fluentui/react-components";
import { ArrowTrendingDown20Regular, DocumentAdd20Regular } from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import Sidebar from "../components/risikoanalyseStep/Sidebar";
import TocMenu from "../components/risikoanalyseStep/TocMenu";
import { useTocLayout } from "../components/risikoanalyseStep/useTocLayout";
import type { SectionDef } from "../components/risikoanalyseStep/types";
import { ContactSelector } from "../components/ContactSelector";
import { EA_CONTRACT_SECTIONS, visibleFields } from "../components/ertragsausfallContractStep/sections";
import FieldRenderer from "../components/ertragsausfallContractStep/FieldRenderer";
import GefahrenTabelle from "../components/ertragsausfallContractStep/GefahrenTabelle";
import AbgleichTabelle from "../components/ertragsausfallContractStep/AbgleichTabelle";
import StepHero from "../components/ertragsausfallContractStep/StepHero";
import { useEaStyles } from "../components/ertragsausfallContractStep/sharedStyles";
import { EA_GEFAHREN } from "../components/ertragsausfallContractStep/gefahren";
import {
  gesamtergebnisVorschlag,
  pruefeAbgleich,
  qualitativeHinweise,
  STATUS_LABEL,
} from "../components/ertragsausfallContractStep/pruefregeln";
import {
  emptyAnswers,
  type ContactSummary,
  type EaAnswers,
  type KbuStatus,
  type RiskOption,
} from "../components/ertragsausfallContractStep/types";

const DRAFT_LS_PREFIX = "ertragsausfall-contract-draft:";
const AUTOSAVE_DEBOUNCE_MS = 1500;
const SIDEBAR_WIDTH = 288;

const useStyles = makeStyles({
  /**
   * Definite Höhenkette statt `minHeight` — nur so bekommt `main` unten eine
   * begrenzte Höhe und wird zum einzigen Scroll-Container. Ohne das stünde die
   * Seitenleiste auf Inhaltshöhe und scrollte mit weg (dieselbe Lösung wie in
   * `NewRiskPage`/`RisikoImmobilieStepForm`: der `<main>` des NavigationLayout
   * hat `overflow: auto` + `minHeight: 0`, also trägt `height: 100%` hier).
   */
  shell: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
  },
  body: { flexGrow: 1, display: "flex", overflow: "hidden", minHeight: 0 },
  /**
   * Flex-Spalte, damit das `<nav>` der Seitenleiste per `flex-grow` bis zum
   * unteren Rand läuft und für sich scrollt. Ob die Leiste überhaupt steht,
   * entscheidet die GEMESSENE Containerbreite (`useTocLayout`) — im
   * schwebenden Fenster wäre eine Viewport-Media-Query systematisch falsch.
   */
  sidebarDesktop: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    minHeight: 0,
    width: `${SIDEBAR_WIDTH}px`,
    ...shorthands.borderRight("1px", "solid", "rgba(45,20,43,0.1)"),
  },
  main: {
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
  },
  inner: {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "1040px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "16px",
    paddingBottom: "24px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    flexGrow: 1,
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: BRAND.white,
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  pageTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: BRAND.ink,
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    flexGrow: 1,
    minWidth: "200px",
  },
  titleChipIcon: { display: "inline-flex", color: BRAND.purple },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.78rem",
    color: BRAND.ink,
    backgroundColor: "#f3f0f5",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("999px"),
  },
  contextPanel: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 720px)": { gridTemplateColumns: "1fr 1fr 1fr" },
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
    paddingTop: "8px",
  },
  contextBlock: { display: "flex", flexDirection: "column", rowGap: "1px", minWidth: 0 },
  contextLabel: {
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  contextValue: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: BRAND.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contextSub: { fontSize: "0.78rem", color: BRAND.muted },
  formPanel: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    paddingTop: "18px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  sectionPill: {
    alignSelf: "flex-start",
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    columnGap: "16px",
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3vw, 2rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "6px",
    marginBottom: 0,
    fontSize: "0.92rem",
    color: BRAND.muted,
  },
  heroBox: { flexShrink: 0 },
  blocker: {
    backgroundColor: "#fdf6e6",
    color: "#7a5a10",
    fontSize: "0.9rem",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#f3d4a3"),
  },
  stickyFooter: { position: "sticky", bottom: 0, zIndex: 12 },
  // Zusammenfassung
  sumGrid: { display: "flex", flexDirection: "column", rowGap: "12px" },
  sumCard: {
    backgroundColor: "#faf8fb",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  sumTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
    marginBottom: "8px",
  },
  sumRow: {
    display: "flex",
    justifyContent: "space-between",
    columnGap: "12px",
    fontSize: "0.86rem",
    paddingTop: "4px",
    paddingBottom: "4px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
  },
  sumKey: { color: BRAND.muted },
  sumValue: { color: BRAND.ink, fontWeight: 600, textAlign: "right" },
});

const buildDisplay = (c: any, fallbackId: number): string => {
  if (!c) return `Kontakt ${fallbackId}`;
  if (typeof c.displayName === "string" && c.displayName.trim()) return c.displayName;
  const first = c.firstName ?? "";
  const last = c.lastname ?? c.lastName ?? "";
  if (last && first) return `${last}, ${first}`;
  if (last) return String(last);
  if (first) return String(first);
  if (c.companyName) return String(c.companyName);
  return `Kontakt ${fallbackId}`;
};

const buildContactSummary = (c: any, fallbackId: number): ContactSummary => ({
  id: c?.id ?? fallbackId,
  displayName: buildDisplay(c, fallbackId),
  street: c?.defaultAddress?.street ?? c?.address?.street,
  postalCode: c?.defaultAddress?.postalCode ?? c?.address?.postalCode,
  city: c?.defaultAddress?.city ?? c?.address?.city,
  country: c?.defaultAddress?.country ?? c?.address?.country,
});

const formatAddr = (c: ContactSummary | null): string => {
  if (!c) return "";
  return [c.street, [c.postalCode, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
};

/**
 * Vertragsmaske Ertragsausfall / Betriebsunterbrechung (separate Deckung).
 *
 * Struktur und Felder folgen der fachlichen Felddefinition „Ertragsausfall
 * Vertragsmaske f. separate Deckung.xlsx“ (8 Abschnitte); das Blatt
 * „Soll-Ist-Abgleich“ liefert die Prüfregeln des letzten Abschnitts.
 *
 * Optik wie die KFZ-/Immobilien-Vertragsmaske, Navigation über die dunkle
 * Seitenleiste der Risiko-Immobilienerfassung.
 */
const ErtragsausfallContractCreatePage = () => {
  const styles = useStyles();
  const ea = useEaStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toasterId = "ertragsausfall-contract-toaster";
  const { dispatchToast } = useToastController(toasterId);

  const initialKontaktId = useMemo<number | null>(() => {
    const fromState = Number((location.state as { kontaktId?: number } | null)?.kontaktId ?? 0);
    if (Number.isFinite(fromState) && fromState > 0) return fromState;
    const fromQuery = Number(searchParams.get("contactId") ?? 0);
    return Number.isFinite(fromQuery) && fromQuery > 0 ? fromQuery : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const artName = useMemo<string | null>(() => {
    const v = (location.state as { artName?: string | null } | null)?.artName;
    return typeof v === "string" && v.trim() ? v : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [answers, setAnswers] = useState<EaAnswers>(emptyAnswers());
  const [risks, setRisks] = useState<RiskOption[]>([]);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [subStepIdx, setSubStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  // Seitenleiste oder Menü-Knopf — entschieden über die gemessene Breite der
  // Schale, nicht über den Viewport (die Maske kann im Fenster laufen).
  const { layout: tocLayout, availableHeight } = useTocLayout(shellRef);

  const sections = EA_CONTRACT_SECTIONS;
  const draftKey = useMemo(
    () => `${DRAFT_LS_PREFIX}${initialKontaktId ?? "new"}`,
    [initialKontaktId],
  );

  const setValue = (key: string, value: string) =>
    setAnswers((p) => ({ ...p, values: { ...p.values, [key]: value } }));

  // Vorausgewählten Kontakt laden
  useEffect(() => {
    if (!initialKontaktId) return;
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`contacts/${initialKontaktId}`));
        if (!res.ok) return;
        const data = await res.json();
        if (aborted) return;
        const summary = buildContactSummary(data, initialKontaktId);
        setAnswers((p) => (p.contact ? p : { ...p, contact: summary }));
      } catch (err) {
        console.error("Kontakt laden fehlgeschlagen", err);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [initialKontaktId]);

  // Risikoobjekte des Kontakts als Betriebsstätten-Auswahl
  useEffect(() => {
    const cid = answers.contact?.id;
    if (!cid) {
      setRisks([]);
      return;
    }
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`risk-objects/by-contact/${cid}`));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const mapped: RiskOption[] = (Array.isArray(data) ? data : [])
          .map((it: any) => ({
            id: Number(it.id),
            name: String(it.name ?? "").trim() || `Risiko ${it.id}`,
            riskType: String(it.riskType ?? "") || undefined,
          }))
          .filter((r) => Number.isFinite(r.id) && r.id > 0);
        if (!aborted) setRisks(mapped);
      } catch (err) {
        console.error("Risikoobjekte laden fehlgeschlagen", err);
        if (!aborted) setRisks([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [answers.contact?.id]);

  // Entwurf einmalig wiederherstellen
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.answers) {
        setAnswers((prev) => ({ ...prev, ...parsed.answers }));
        const sec = Math.min(Math.max(0, parsed.sectionIdx ?? 0), sections.length - 1);
        const maxStep = (sections[sec]?.steps.length ?? 1) - 1;
        setSectionIdx(sec);
        setSubStepIdx(Math.min(Math.max(0, parsed.subStepIdx ?? 0), maxStep));
        setDraftSavedAt(parsed.savedAt ?? null);
      }
    } catch {
      // ignore
    }
  }, [draftKey, sections]);

  // Autosave
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const savedAt = new Date().toISOString();
      try {
        localStorage.setItem(draftKey, JSON.stringify({ answers, sectionIdx, subStepIdx, savedAt }));
        setDraftSavedAt(savedAt);
      } catch {
        // ignore
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [answers, sectionIdx, subStepIdx, draftKey]);

  const section = sections[sectionIdx] ?? sections[0];
  const step = section.steps[subStepIdx] ?? section.steps[0];
  const accent = sectionColorHex[section.color];

  const totalSteps = useMemo(
    () => sections.reduce((acc, s) => acc + s.steps.length, 0),
    [sections],
  );
  const stepIndex = useMemo(() => {
    let idx = 0;
    for (let s = 0; s < sectionIdx; s++) idx += sections[s].steps.length;
    return idx + subStepIdx;
  }, [sections, sectionIdx, subStepIdx]);
  const progressFraction = totalSteps > 1 ? stepIndex / (totalSteps - 1) : 0;
  const isLastStep = stepIndex === totalSteps - 1;

  // Seitenleiste erwartet das Abschnittsmodell der Risikoanalyse.
  const sidebarSections: SectionDef[] = useMemo(
    () =>
      sections.map((s) => ({
        key: s.key,
        title: s.shortTitle,
        color: s.color,
        subSteps: s.steps.map((st) => ({ key: st.key, title: st.title, questions: [] })),
      })),
    [sections],
  );

  const scrollTop = () => mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  const goTo = (sIdx: number, ssIdx: number) => {
    setSectionIdx(sIdx);
    setSubStepIdx(ssIdx);
    scrollTop();
  };
  const goPrev = () => {
    if (subStepIdx > 0) return goTo(sectionIdx, subStepIdx - 1);
    if (sectionIdx > 0) return goTo(sectionIdx - 1, sections[sectionIdx - 1].steps.length - 1);
  };
  const goNext = () => {
    if (subStepIdx < section.steps.length - 1) return goTo(sectionIdx, subStepIdx + 1);
    if (sectionIdx + 1 < sections.length) return goTo(sectionIdx + 1, 0);
  };

  const saveDraftNow = () => {
    setSavingDraft(true);
    const savedAt = new Date().toISOString();
    try {
      localStorage.setItem(draftKey, JSON.stringify({ answers, sectionIdx, subStepIdx, savedAt }));
      setDraftSavedAt(savedAt);
    } catch {
      // ignore
    } finally {
      setTimeout(() => setSavingDraft(false), 250);
    }
  };

  const ergebnisse = useMemo(() => pruefeAbgleich(answers, new Date()), [answers]);
  const hinweise = useMemo(() => qualitativeHinweise(answers), [answers]);
  const vorschlag = useMemo(() => gesamtergebnisVorschlag(ergebnisse), [ergebnisse]);

  const stepBlocker = !answers.contact && step.kind !== "grunddaten"
    ? "Bitte zuerst den Versicherungsnehmer auswählen."
    : null;

  const onSubmit = async () => {
    if (!answers.contact) {
      dispatchToast(
        <Toast>
          <ToastTitle>Bitte einen Kontakt auswählen</ToastTitle>
        </Toast>,
        { intent: "warning" },
      );
      return;
    }
    setSubmitting(true);
    try {
      const characteristics: Array<{ nameBezeichnung: string; contractCharacteristicValue: string }> =
        [];
      const push = (name: string, value: string | null | undefined) => {
        if (value === null || value === undefined || String(value).trim() === "") return;
        characteristics.push({ nameBezeichnung: name, contractCharacteristicValue: String(value) });
      };

      for (const [key, value] of Object.entries(answers.values)) push(`ea.${key}`, value);
      for (const g of EA_GEFAHREN) push(`ea.gefahr.${g.key}`, answers.kbu[g.key]);
      if (answers.betriebsstaetten.length) {
        push("ea.betriebsstaetten", answers.betriebsstaetten.join(","));
      }
      push("ea.abgleich_gesamtergebnis", answers.values["gesamtergebnis"] ?? vorschlag);
      push(
        "ea.abgleich_json",
        JSON.stringify(ergebnisse.map((e) => ({ k: e.key, s: e.status }))),
      );
      if (artName) push("ea.artName", artName);

      const risikoOrt =
        risks.find((r) => r.id === answers.betriebsstaetten[0])?.name ??
        formatAddr(answers.contact) ??
        "unbekannt";

      const payload: Record<string, any> = {
        contractNbr: (answers.values["vertragsnummer"] ?? "").trim() || `EA-${Date.now()}`,
        insuranceNbr: (answers.values["versicherungsnummer"] ?? "").trim() || "unbekannt",
        branch: artName ?? "Ertragsausfall",
        riskLocation: risikoOrt || "unbekannt",
        contact: { id: answers.contact.id },
        characteristics,
      };

      const res = await fetch(apiUrl("contracts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        console.error("Contract POST failed", res.status, await res.text());
        dispatchToast(
          <Toast>
            <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
          </Toast>,
          { intent: "error" },
        );
        return;
      }
      let createdId: number | null = null;
      try {
        createdId = (await res.json())?.id ?? null;
      } catch {
        // ignore
      }
      try {
        localStorage.removeItem(draftKey);
      } catch {
        // ignore
      }
      dispatchToast(
        <Toast>
          <ToastTitle>Ertragsausfall-Vertrag erstellt</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
      if (createdId) navigate(`/vertraege/edit/${createdId}`);
      else navigate(`/contacts/${answers.contact.id}`, { state: { defaultTab: "vertraege" } });
    } catch (e) {
      console.error("Contract POST threw", e);
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Schritt-Inhalte ───────────────────────────────────────────────────
  const fieldGrid = (
    <div className={ea.fieldGrid}>
      {visibleFields(step.fields, answers.values).map((f) => (
        <FieldRenderer key={f.key} field={f} values={answers.values} onChange={setValue} />
      ))}
    </div>
  );

  const renderStep = () => {
    switch (step.kind) {
      case "grunddaten":
        return (
          <div className={ea.stepBody}>
            <ContactSelector
              label="Versicherungsnehmer"
              required
              selectedContact={answers.contact}
              onSelect={(c: any) =>
                setAnswers((p) => ({ ...p, contact: c ? buildContactSummary(c, c.id) : null }))
              }
            />
            {fieldGrid}
          </div>
        );

      case "umfeld":
        return (
          <div className={ea.stepBody}>
            {fieldGrid}
            <div className={mergeClasses(ea.field, ea.fieldSpan2)}>
              <div className={ea.labelRow}>
                <span className={ea.label}>Versicherte Betriebsstätten / Risikoorte</span>
                <span className={ea.reqBadge}>Verknüpfung statt Doppeleingabe</span>
              </div>
              {risks.length ? (
                <div className={ea.pickList}>
                  {risks.map((r) => {
                    const active = answers.betriebsstaetten.includes(r.id);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        className={mergeClasses(ea.pickTile, active && ea.pickTileActive)}
                        onClick={() =>
                          setAnswers((p) => ({
                            ...p,
                            betriebsstaetten: active
                              ? p.betriebsstaetten.filter((id) => id !== r.id)
                              : [...p.betriebsstaetten, r.id],
                          }))
                        }
                      >
                        <span className={mergeClasses(ea.pickMark, active && ea.pickMarkActive)}>
                          {active ? "✓" : ""}
                        </span>
                        <span>
                          <span className={ea.pickName}>{r.name}</span>
                          {r.riskType && <span className={ea.pickMeta}> · {r.riskType}</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className={ea.emptyNote}>
                  {answers.contact
                    ? "Für diesen Kontakt sind keine Risikoobjekte erfasst."
                    : "Zuerst den Versicherungsnehmer wählen."}
                </div>
              )}
              <span className={ea.sourceRow}>Quelle: Risikoobjekte / Vertrag</span>
              <span className={ea.checkRow}>Abgleich mit kritischen Standorten</span>
            </div>
          </div>
        );

      case "gefahren":
        return (
          <div className={ea.stepBody}>
            <GefahrenTabelle
              value={answers.kbu}
              onChange={(next: Record<string, KbuStatus>) =>
                setAnswers((p) => ({ ...p, kbu: next }))
              }
            />
            {fieldGrid}
          </div>
        );

      case "ergebnis":
        return (
          <div className={ea.stepBody}>
            <AbgleichTabelle ergebnisse={ergebnisse} hinweise={hinweise} />
            <div className={mergeClasses(ea.field, ea.fieldSpan2)}>
              <div className={ea.labelRow}>
                <span className={ea.label}>Gesamtergebnis</span>
                <span className={mergeClasses(ea.reqBadge, ea.reqBadgeAuto)}>
                  Automatisch + Berater
                </span>
              </div>
              <select
                className={mergeClasses(ea.input, ea.select)}
                value={answers.values["gesamtergebnis"] ?? ""}
                onChange={(e) => setValue("gesamtergebnis", e.target.value)}
              >
                <option value="">
                  Systemvorschlag übernehmen: {STATUS_LABEL[vorschlag]}
                </option>
                <option value="passend">passend</option>
                <option value="pruefen">prüfen</option>
                <option value="luecke">Deckungslücke</option>
              </select>
              <span className={ea.sourceRow}>Quelle: System + Beraterfreigabe</span>
              <span className={ea.checkRow}>Zusammenfassung aller Einzelprüfungen</span>
            </div>
            {renderSummary()}
          </div>
        );

      default:
        return <div className={ea.stepBody}>{fieldGrid}</div>;
    }
  };

  /** Kompakte Zusammenfassung aller erfassten Felder, gruppiert nach Abschnitt. */
  const renderSummary = () => (
    <div className={styles.sumGrid}>
      {sections.map((s) => {
        const rows: Array<[string, string]> = [];
        for (const st of s.steps) {
          for (const f of st.fields ?? []) {
            const v = answers.values[f.key];
            const parts = (f.parts ?? [])
              .map((p) => {
                const pv = answers.values[`${f.key}.${p.key}`];
                return pv ? `${p.label}: ${pv}` : null;
              })
              .filter(Boolean) as string[];
            if (f.type === "coverage") {
              if (!v) continue;
              rows.push([
                f.label,
                v === "ja" ? ["Ja", ...parts].join(" · ") : "Nein",
              ]);
              continue;
            }
            if (f.type === "composite") {
              if (!parts.length) continue;
              rows.push([f.label, parts.join(" · ")]);
              continue;
            }
            if (!v) continue;
            rows.push([f.label, f.type === "tags" ? v.split("|").join(", ") : v]);
          }
        }
        if (s.key === "gefahren") {
          const gesetzt = EA_GEFAHREN.filter((g) => answers.kbu[g.key]);
          if (gesetzt.length) {
            rows.unshift([
              "KBU-Status je Gefahr",
              gesetzt.map((g) => `${g.dsi}=${answers.kbu[g.key]}`).join(", "),
            ]);
          }
        }
        if (s.key === "grunddaten" && answers.betriebsstaetten.length) {
          rows.push([
            "Versicherte Betriebsstätten",
            risks
              .filter((r) => answers.betriebsstaetten.includes(r.id))
              .map((r) => r.name)
              .join(", "),
          ]);
        }
        if (!rows.length) return null;
        return (
          <div key={s.key} className={styles.sumCard}>
            <div className={styles.sumTitle} style={{ color: sectionColorHex[s.color] }}>
              {s.title}
            </div>
            {rows.map(([k, v]) => (
              <div key={k} className={styles.sumRow}>
                <span className={styles.sumKey}>{k}</span>
                <span className={styles.sumValue}>{v}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );

  const tocProps = {
    sections: sidebarSections,
    currentSectionIdx: sectionIdx,
    currentSubStepIdx: subStepIdx,
    onSelect: goTo,
    progressFraction,
    progressDetail: `Schritt ${stepIndex + 1}/${totalSteps}`,
    eyebrow: "Ertragsausfall",
    heading: "Vertragsmaske",
    ariaLabel: "Abschnitte der Ertragsausfall-Vertragsmaske",
  };

  return (
    <div className={styles.shell} ref={shellRef}>
      <Toaster toasterId={toasterId} />
      <div className={styles.body}>
        {tocLayout === "sidebar" && (
          <aside className={styles.sidebarDesktop}>
            <Sidebar {...tocProps} />
          </aside>
        )}

        <main className={styles.main} ref={mainRef}>
          <div className={styles.inner}>
            {tocLayout === "menu" && (
              <TocMenu
                {...tocProps}
                maxHeight={availableHeight}
                kontextText={`Schritt ${stepIndex + 1} von ${totalSteps} · ${section.title}`}
              />
            )}
            <div className={styles.stickyHeader}>
              <div className={styles.headerRow}>
                <h1 className={styles.pageTitle}>
                  <span className={styles.titleChipIcon} aria-hidden="true">
                    <DocumentAdd20Regular />
                  </span>
                  Ertragsausfall-Vertrag anlegen
                </h1>
                <Button appearance="subtle" onClick={() => navigate(-1)}>
                  Zurück
                </Button>
              </div>
              <div className={styles.chips}>
                <span className={styles.chip}>
                  <span className={styles.titleChipIcon} aria-hidden="true">
                    <ArrowTrendingDown20Regular />
                  </span>
                  {artName ?? "Ertragsausfall / Betriebsunterbrechung"}
                </span>
                {answers.values["deckungsart"] && (
                  <span className={styles.chip}>{answers.values["deckungsart"]}</span>
                )}
                {answers.values["haftzeit"] && (
                  <span className={styles.chip}>Haftzeit {answers.values["haftzeit"]} Monate</span>
                )}
                <span className={styles.chip}>Abgleich: {STATUS_LABEL[vorschlag]}</span>
              </div>
              <div className={styles.contextPanel}>
                <div className={styles.contextBlock}>
                  <span className={styles.contextLabel}>Versicherungsnehmer</span>
                  <span className={styles.contextValue}>
                    {answers.contact?.displayName ?? "—"}
                  </span>
                  <span className={styles.contextSub}>{formatAddr(answers.contact) || "—"}</span>
                </div>
                <div className={styles.contextBlock}>
                  <span className={styles.contextLabel}>Betriebsstätten</span>
                  <span className={styles.contextValue}>
                    {answers.betriebsstaetten.length
                      ? `${answers.betriebsstaetten.length} ausgewählt`
                      : "Keine"}
                  </span>
                  <span className={styles.contextSub}>
                    {risks
                      .filter((r) => answers.betriebsstaetten.includes(r.id))
                      .map((r) => r.name)
                      .join(", ") || "—"}
                  </span>
                </div>
                <div className={styles.contextBlock}>
                  <span className={styles.contextLabel}>Versicherungssumme</span>
                  <span className={styles.contextValue}>
                    {answers.values["vs_kbu"] ? `${answers.values["vs_kbu"]} EUR` : "—"}
                  </span>
                  <span className={styles.contextSub}>
                    {answers.values["bemessungsgrundlage"] || "Bemessungsgrundlage offen"}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.formPanel}>
              <span className={styles.sectionPill} style={{ backgroundColor: accent }}>
                {sectionIdx + 1}. {section.title}
              </span>
              <div className={styles.titleRow}>
                <div style={{ flexGrow: 1, minWidth: 200 }}>
                  <h2 className={styles.title}>{step.title}</h2>
                  {step.description && <p className={styles.description}>{step.description}</p>}
                </div>
                <div className={styles.heroBox}>
                  <StepHero kind={step.kind} />
                </div>
              </div>
              {stepBlocker ? <div className={styles.blocker}>{stepBlocker}</div> : renderStep()}
            </div>
          </div>

          <div className={styles.stickyFooter}>
            <BottomBar
              sticky
              onPrev={goPrev}
              onNext={goNext}
              onSubmit={onSubmit}
              onSaveDraft={saveDraftNow}
              canPrev={stepIndex > 0}
              canNext={!stepBlocker}
              nextDisabledTitle={stepBlocker ?? undefined}
              isLastStep={isLastStep}
              submitting={submitting}
              savingDraft={savingDraft}
              draftSavedAt={draftSavedAt}
              progressLabel={`Schritt ${stepIndex + 1} von ${totalSteps} · ${section.title}`}
              progressFraction={progressFraction}
              submitLabel="Vertrag anlegen"
              submittingLabel="Wird angelegt…"
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ErtragsausfallContractCreatePage;
